import { MODELS, useMultiModel } from "../../../api";
import type { WorkshopStepInfo } from "../../../api/workshop";
import type { UserId } from "../../../api/user";
import { canManageWorkshop, Roles } from "../../../lib/Roles";
import NotAuthorizedPage from "../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";
import UserDisplay from "../../../design-system/primitives/components/UserDisplay/UserDisplay";
import InvitePanel from "./InvitePanel";
import InviteTeachersButton from "./InviteTeachersButton";
import TeacherRow from "./TeacherRow";
import styles from "./TeachersOverview.css";

/**
 * The colleagues tab: one panel with the invite action, pending email invites
 * and the teachers already in the workshop (owner first).
 */
const TeachersOverview = ({ workshop }: { workshop: WorkshopStepInfo }) => {
  const [me, userInfo, pendingInvites] = useMultiModel([
    MODELS.ME,
    MODELS.WORKSHOP_USER_INFO(workshop.id),
    MODELS.WORKSHOP_PENDING_INVITES(workshop.id),
  ]);

  if (!canManageWorkshop(workshop.roles)) {
    return <NotAuthorizedPage />;
  }

  const isOwner = (userId: UserId) => workshop.owner?.id === userId;

  const teachers = (userInfo?.participants ?? [])
    .filter((participant) => participant.role === Roles.TEACHER)
    .sort((a, b) => Number(isOwner(b.id)) - Number(isOwner(a.id)));

  // Only admins or the owner may transfer ownership or remove colleagues — or
  // anyone who can manage the workshop, while it has no owner.
  const canAdminister = Boolean(
    me &&
    ((!me.isAnon && me.isAdmin) ||
      !workshop.owner ||
      workshop.owner.id === me.id),
  );

  return (
    <InvitePanel
      title="Kolleg*innen einladen"
      description="Lade deine Kolleg*innen ein, damit sie die Lernreise mit verwalten können."
      action={<InviteTeachersButton workshopId={workshop.id} />}
      actionPosition="header"
      background="stars"
    >
      <div className={styles.sections}>
        {pendingInvites.length > 0 && (
          <section className={styles.section}>
            <Text variant="label" color="neutral-100">
              Ausstehende Einladungen
            </Text>
            {pendingInvites.map((invite) => (
              <UserDisplay
                key={invite.id}
                user={{ name: invite.email }}
                surface="base"
              />
            ))}
          </section>
        )}

        <section className={styles.section}>
          {teachers.map((teacher) => (
            <TeacherRow
              key={teacher.id}
              teacher={teacher}
              workshop={workshop}
              isOwner={isOwner(teacher.id)}
              canTransferOwnership={canAdminister && !isOwner(teacher.id)}
              canRemove={canAdminister && teacher.id !== me?.id}
            />
          ))}
        </section>
      </div>
    </InvitePanel>
  );
};

export default TeachersOverview;
